import type { PermissionAction } from '@/shared/auth/types'
import type { IconName } from './icons'

/**
 * The shape of a module's sidebar menu, and the helpers every module shell
 * uses to read it.
 *
 * Each module owns its own menu (Billing's is features/billing/nav.ts) and
 * registers it in modules.ts. This file knows nothing about any one module, so
 * a new module gets filtering, the active page and the breadcrumb for free.
 *
 * What a sidebar may contain is a convention, written up in
 * docs/konvensi-ui.md: pages of this module only. Beranda and the other
 * modules are reached from the module switcher in the top bar.
 */

export interface NavItem {
  label: string
  /** Internal route. Mutually exclusive with href. */
  to?: string
  /** Another application. Opens in a new tab and is labelled as such. */
  href?: string
  icon: IconName
  /** Omit to make the item always visible. */
  permCode?: string
  action?: PermissionAction
  /** Only match the exact path, for items whose route has children. */
  end?: boolean
}

export interface NavSection {
  title?: string
  items: NavItem[]
}

/**
 * A count beside a menu item: how many things on that page want this person.
 *
 * Only counts of work, never totals -- "Proforma 1.284" tells nobody anything,
 * "Persetujuan 4" tells them where to go next. `attention` is for work waiting
 * on them (red), `info` for things to read (neutral).
 */
export interface NavBadge {
  count: number
  tone: 'attention' | 'info'
}

/** Badges keyed by the item's `to`. A missing key or a zero count shows nothing. */
export type NavBadges = Partial<Record<string, NavBadge>>

type CanFn = (permCode: string, action?: PermissionAction) => boolean

/**
 * Items the user has no permission for are HIDDEN, never disabled: a control
 * that can never be enabled is an invitation to file a ticket. Sections with
 * nothing visible in them disappear entirely -- otherwise a user without any
 * settings permission sees a "Pengaturan" heading over empty space.
 */
export function visibleSections(sections: NavSection[], can: CanFn): NavSection[] {
  return sections
    .map((section) => ({
      ...section,
      items: section.items.filter((item) => !item.permCode || can(item.permCode, item.action ?? 'view')),
    }))
    .filter((section) => section.items.length > 0)
}

/** The deepest item matching the current path, for the page heading and title. */
export function activeItem(sections: NavSection[], pathname: string): NavItem | undefined {
  const candidates = sections
    .flatMap((s) => s.items)
    .filter((item) => {
      if (!item.to) return false
      return item.end ? pathname === item.to : pathname === item.to || pathname.startsWith(item.to + '/')
    })
  return candidates.sort((a, b) => (b.to?.length ?? 0) - (a.to?.length ?? 0))[0]
}

/** The section an item sits in, for the breadcrumb above the page title. */
export function sectionOf(sections: NavSection[], item: NavItem): NavSection | undefined {
  return sections.find((section) => section.items.includes(item))
}
